import React, { useState } from "react";
import { formatCurrencyCompact, formatPercent } from "../../utils/formatters";
import { DEMO_FI_NUMBER, DEMO_NET_WORTH } from "./demoData";

export default function DemoFiNumberCard() {
    const [showSettings, setShowSettings] = useState(false);
    const [rateInput, setRateInput] = useState("4.0");
    const [expensesInput, setExpensesInput] = useState("");
    const [overridden, setOverridden] = useState(false);

    const withdrawalRate = parseFloat(rateInput) || 4.0;
    const clampedRate = Math.min(Math.max(withdrawalRate, 1.0), 10.0);

    const baseAnnualExpenses = DEMO_FI_NUMBER.calculatedAnnualExpenses;
    const annualExpenses = overridden && parseFloat(expensesInput) > 0
        ? parseFloat(expensesInput)
        : baseAnnualExpenses;

    const fiNumber = Math.round(annualExpenses / (clampedRate / 100));
    const netWorth = DEMO_NET_WORTH.netWorth;
    const progress = fiNumber > 0 ? Math.min((netWorth / fiNumber) * 100, 100) : 0;
    const remaining = Math.max(fiNumber - netWorth, 0);

    const annualSavings = DEMO_FI_NUMBER.annualSavings;
    let yearsToFi = null;
    if (annualSavings > 0 && remaining > 0) {
        const realReturn = 0.07;
        let balance = netWorth;
        let years = 0;
        while (balance < fiNumber && years < 100) {
            balance = balance * (1 + realReturn) + annualSavings;
            years++;
        }
        if (years < 100) yearsToFi = years;
    }

    const handleSave = () => {
        const expVal = parseFloat(expensesInput);
        if (!isNaN(expVal) && expVal > 0) {
            setOverridden(true);
        }
        setShowSettings(false);
    };

    const handleClear = () => {
        setExpensesInput("");
        setOverridden(false);
    };

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">FI Number</h3>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{formatPercent(clampedRate)} withdrawal rate</span>
                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className="text-gray-400 hover:text-gray-700 transition-colors"
                        title="Configure FI settings"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>

            {showSettings && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Withdrawal Rate (%)</label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="number"
                                    value={rateInput}
                                    onChange={(e) => setRateInput(e.target.value)}
                                    min="1"
                                    max="10"
                                    step="0.1"
                                    placeholder="4.0"
                                    className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-accent-500 focus:outline-none"
                                />
                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-gray-500 block mb-1">
                            Annual Expenses Override
                            <span className="text-gray-400 ml-1">(blank = auto)</span>
                        </label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                <input
                                    type="number"
                                    value={expensesInput}
                                    onChange={(e) => setExpensesInput(e.target.value)}
                                    placeholder={String(Math.round(baseAnnualExpenses))}
                                    className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 pl-6 text-sm text-gray-900 placeholder-gray-400 focus:border-accent-500 focus:outline-none"
                                />
                            </div>
                            {overridden && (
                                <button
                                    onClick={handleClear}
                                    className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleSave}
                        className="w-full px-3 py-1.5 bg-accent-600 hover:bg-accent-500 text-white text-sm rounded transition-colors"
                    >
                        Save
                    </button>
                </div>
            )}

            <div className="text-3xl font-bold text-gray-900 mb-1">
                {formatCurrencyCompact(fiNumber)}
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Based on {formatCurrencyCompact(annualExpenses)}/yr in expenses
                {overridden ? (
                    <span className="text-amber-600 ml-1">(manual)</span>
                ) : (
                    <span className="text-gray-500 ml-1">(auto)</span>
                )}
            </p>

            <div className="mb-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{formatPercent(progress)} there</span>
                    <span>{formatCurrencyCompact(remaining)} to go</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-accent-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {yearsToFi !== null && (
                <p className="text-sm text-gray-700">
                    At your current savings rate, you could reach FI in{" "}
                    <span className="text-accent-600 font-semibold">
                        ~{yearsToFi} {yearsToFi === 1 ? "year" : "years"}
                    </span>
                </p>
            )}
        </div>
    );
}
